import type { Error, Validator } from '@ps/web-shared-validators';
import type { Required } from 'utility-types';

import { request as apiRequest } from '@swf/core/api/request';
import type { RequestOptions } from '@swf/core/types/Request';

import {
  TXTCLI_Your_must_enter_a_valid_DNIX,
  TXTWCL_Your_must_enter_a_valid_NIE
} from './taxCode.messages.json';

const NIE_REGEX = /^[XYZ][\d]{7}[TRWAGMYFPDXBNJZSQVHLCKET]$/i;
const DNI_CONTROL_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE';
const DNI_REGEX = /^\d{8}[A-Z]$/;

export const validateNIE = (value: string): Error => {
  const replaceNieLetter = (nie: string): string | null => {
    if (!nie || nie.length === 0) return null;
    const nieLetter = nie.charAt(0).toUpperCase();
    if (nieLetter === 'X') return '0' + nie.substring(1);
    if (nieLetter === 'Y') return '1' + nie.substring(1);
    if (nieLetter === 'Z') return '2' + nie.substring(1);
    return null;
  };

  const nieReplaced = replaceNieLetter(value);

  if (!nieReplaced) {
    return {
      valid: false,
      message: TXTCLI_Your_must_enter_a_valid_DNIX
    };
  }

  const isNieValid =
    NIE_REGEX.test(value) &&
    (() => {
      const dni = nieReplaced;
      const dniNumber = dni.slice(0, -1).replace(/[^\d]/g, '');
      const letterIndex = +dniNumber % 23;
      const letter = dni.slice(-1).toUpperCase();
      return DNI_CONTROL_LETTERS.charAt(letterIndex) === letter;
    })();

  if (!isNieValid) {
    return {
      valid: false,
      message: TXTWCL_Your_must_enter_a_valid_NIE
    };
  }
  return { valid: true };
};

export const validateDNI = (value: string): Error => {
  if (!value || value.length === 0 || !DNI_REGEX.test(value)) {
    return {
      valid: false,
      message: TXTCLI_Your_must_enter_a_valid_DNIX
    };
  }

  const isDniValid = (() => {
    const dni = value;
    const dniNumber = dni.slice(0, -1).replace(/[^\d]/g, '');
    const letterIndex = +dniNumber % 23;
    const letter = dni.slice(-1).toUpperCase();
    return DNI_CONTROL_LETTERS.charAt(letterIndex) === letter;
  })();

  if (!isDniValid) {
    return {
      valid: false,
      message: TXTCLI_Your_must_enter_a_valid_DNIX
    };
  }

  return { valid: true };
};

export const checkTaxCode = (
  options: Required<Partial<RequestOptions>, 'meta'>,
  message: Error['message']
) => {
  const isUnique: Validator = async (value: string): Promise<Error> => {
    if (value) {
      const {
        body: { unique }
      } = await apiRequest({
        ...options,
        service: 'wc3.user.check.fiscalCode',
        body: { fiscalCode: value }
      });

      return unique ? { valid: true } : { valid: false, message };
    }

    return { valid: false, message };
  };
  return isUnique as Validator;
};
